'use client';

import { useState, useMemo } from 'react';
import { Info, Calculator, TrendingUp, Wallet } from 'lucide-react';
import { formatUSD, cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { V2Grid, V2GridAsset } from '@/lib/types/v2';
import { V2_CONSTANTS } from '@/lib/types/v2';

interface EconomicsPanelProps {
  grid: V2Grid;
  assets: V2GridAsset[];
  floorPrice: number;
  yieldTokenSymbol?: string;
}

export function EconomicsPanel({ grid, assets, floorPrice, yieldTokenSymbol }: EconomicsPanelProps) {
  const symbol = yieldTokenSymbol || 'ETH';
  return (
    <div className="space-y-4">
      <BreakevenCalculator grid={grid} floorPrice={floorPrice} />
      <TaxCostExplainer grid={grid} />
      <YieldSourcesSummary assets={assets} grid={grid} yieldTokenSymbol={symbol} />
    </div>
  );
}

function BreakevenCalculator({ grid, floorPrice }: { grid: V2Grid; floorPrice: number }) {
  const [buyPrice, setBuyPrice] = useState<string>(floorPrice > 0 ? floorPrice.toFixed(2) : '10');

  const buyPriceNum = parseFloat(buyPrice) || 0;
  const taxRateWeekly = (grid.taxRateBps / V2_CONSTANTS.TAX_RATE_DENOMINATOR);
  const weeklyTax = buyPriceNum * taxRateWeekly;

  const totalYieldPerSeat = Number(grid.totalYieldReceived) / 1e18 / grid.totalSeats;
  const gridAgeWeeks = Math.max(1, (Date.now() / 1000 - grid.createdAt) / V2_CONSTANTS.SECONDS_PER_WEEK);
  const avgWeeklyYieldPerSeat = totalYieldPerSeat / gridAgeWeeks;

  const weeksToBreakeven = weeklyTax > 0
    ? (avgWeeklyYieldPerSeat > weeklyTax ? buyPriceNum / (avgWeeklyYieldPerSeat - weeklyTax) : Infinity)
    : Infinity;

  return (
    <div className="rounded-lg border p-3 space-y-3">
      <div className="flex items-center gap-2">
        <Calculator className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold">Breakeven Calculator</h3>
      </div>

      <div className="space-y-2">
        <label className="text-xs text-muted-foreground">Your buy price (USDC)</label>
        <input
          type="number"
          value={buyPrice}
          onChange={(e) => setBuyPrice(e.target.value)}
          className="w-full rounded-md border bg-transparent px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-ring"
          min="0"
          step="0.01"
        />
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="rounded-md bg-muted/30 p-2">
          <p className="text-muted-foreground">Weekly tax</p>
          <p className="font-mono font-medium text-red-400">
            -{formatUSD(weeklyTax)}<span className="text-muted-foreground">/wk</span>
          </p>
        </div>
        <div className="rounded-md bg-muted/30 p-2">
          <p className="text-muted-foreground">Avg yield/seat</p>
          <p className="font-mono font-medium text-win-green">
            +{formatUSD(avgWeeklyYieldPerSeat)}<span className="text-muted-foreground">/wk</span>
          </p>
        </div>
      </div>

      <div className="rounded-md bg-muted/30 p-2 text-center">
        <p className="text-xs text-muted-foreground">Estimated breakeven</p>
        <p className={cn(
          "text-sm font-bold",
          weeksToBreakeven === Infinity ? "text-red-400" : weeksToBreakeven <= 4 ? "text-win-green" : "text-amber-400"
        )}>
          {weeksToBreakeven === Infinity
            ? 'Tax exceeds yield'
            : `~${Math.ceil(weeksToBreakeven)} weeks`
          }
        </p>
      </div>
    </div>
  );
}

function TaxCostExplainer({ grid }: { grid: V2Grid }) {
  const taxRate = grid.taxRateBps / 100;
  const protocolSplit = 20;
  const creatorSplit = 80;

  return (
    <TooltipProvider>
      <div className="rounded-lg border p-3 space-y-2">
        <div className="flex items-center gap-2">
          <Wallet className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold">Tax Cost</h3>
        </div>

        <p className="text-xs text-muted-foreground">
          Each seat is taxed at <span className="font-mono text-foreground">{taxRate}%</span> of
          its self-assessed price per week. Tax is deducted from your deposit.
        </p>

        <div className="space-y-1.5 text-xs">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              <span className="text-muted-foreground">Tax rate</span>
              <Tooltip>
                <TooltipTrigger>
                  <Info className="h-3 w-3 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent className="max-w-[200px]">
                  <p className="text-xs">Applied per week on self-assessed price. Higher prices = higher tax but harder to buy out.</p>
                </TooltipContent>
              </Tooltip>
            </div>
            <span className="font-mono">{taxRate}%/wk</span>
          </div>
          <div className="flex items-center justify-between text-muted-foreground">
            <span>Creator share</span>
            <span className="font-mono">{creatorSplit}%</span>
          </div>
          <div className="flex items-center justify-between text-muted-foreground">
            <span>Protocol share</span>
            <span className="font-mono">{protocolSplit}%</span>
          </div>
        </div>

        <div className="h-2 rounded-full overflow-hidden flex bg-muted/50 mt-2">
          <div className="bg-primary/70 h-full" style={{ width: `${creatorSplit}%` }} />
          <div className="bg-muted-foreground/30 h-full" style={{ width: `${protocolSplit}%` }} />
        </div>
      </div>
    </TooltipProvider>
  );
}

function YieldSourcesSummary({ assets, grid, yieldTokenSymbol }: { assets: V2GridAsset[]; grid: V2Grid; yieldTokenSymbol: string }) {
  const activeAssets = assets.filter(a => a.active);
  const divisor = 10 ** (grid.yieldTokenDecimals ?? 18);
  const totalYield = Number(grid.totalYieldReceived) / divisor;
  const total7d = activeAssets.reduce((sum, a) => sum + Number(a.yield7d) / divisor, 0);

  return (
    <div className="rounded-lg border p-3 space-y-2">
      <div className="flex items-center gap-2">
        <TrendingUp className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold">Yield Sources</h3>
      </div>

      <p className="text-xs text-muted-foreground">
        This grid earns yield from <span className="font-mono text-foreground">{activeAssets.length}</span> active
        {activeAssets.length === 1 ? ' asset' : ' assets'}. Yield is split equally across all 100 seats.
      </p>

      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="rounded-md bg-muted/30 p-2">
          <p className="text-muted-foreground">Total yield</p>
          <p className="font-mono font-medium text-win-green">{totalYield.toFixed(4)} {yieldTokenSymbol}</p>
        </div>
        <div className="rounded-md bg-muted/30 p-2">
          <p className="text-muted-foreground">7D yield</p>
          <p className="font-mono font-medium text-win-green">{total7d.toFixed(4)} {yieldTokenSymbol}</p>
        </div>
      </div>

      <p className="text-[10px] text-muted-foreground">
        Yield vs. tile sales: Seats earn 1% of LP fees (yield) continuously.
        Buyouts (tile sales) are one-time events where a new holder pays the self-assessed price.
      </p>
    </div>
  );
}
